/**
 * Job Runs pure helpers (W4b-β2) — extracted from JobRunsViewer.tsx so the run-status
 * tone/label mapping and the search .or() construction are unit-testable regression guards
 * AND the single source of truth the page renders. Mirrors buildTracker.ts.
 */

import { escapeOrIlikeTerm } from '../../../../src/lib/postgrestEscape';

export type RunTone = 'success' | 'danger' | 'neutral';

// job_runs rows carry ok (boolean) + detail.phase ('started' | 'complete' | 'error').
// The worker writes its start row ok=false / phase='started' and only finalizes ok at the
// end, so an ok=false row still at phase='started' is in-flight OR a worker that DIED
// mid-run → neutral, NOT danger. ok=true → success; a finalized ok=false → danger.
export function runTone(ok: boolean, phase: string | null): RunTone {
  if (ok) return 'success';
  if (phase === 'started') return 'neutral';
  return 'danger';
}

export function runLabel(ok: boolean, phase: string | null): string {
  if (ok) return 'OK';
  if (phase === 'started') return 'Running / died';
  if (phase === 'error') return 'Error';
  return 'Failed';
}

// A compact one-line summary of the detail jsonb for the grid (workers vary in their count
// keys; attempted + errors are the near-universal pair). Falls back to '—' when absent.
export function runSummary(detail: Record<string, unknown> | null): string {
  if (!detail) return '—';
  const parts: string[] = [];
  if (typeof detail.attempted === 'number') parts.push(`${detail.attempted} attempted`);
  if (typeof detail.errors === 'number' && detail.errors > 0) parts.push(`${detail.errors} err`);
  return parts.length ? parts.join(' · ') : '—';
}

// The numeric/scalar count entries of the detail jsonb (phase/error rendered separately) —
// rendered as label:value rows in the detail panel. Generic across all worker shapes.
export function runDetailEntries(
  detail: Record<string, unknown> | null
): Array<{ key: string; value: string }> {
  if (!detail) return [];
  return Object.entries(detail)
    .filter(([k, v]) => k !== 'phase' && k !== 'error' && (typeof v === 'number' || typeof v === 'boolean'))
    .map(([key, v]) => ({ key, value: String(v) }));
}

// Search across the one text column job_runs exposes (job_name). escapeOrIlikeTerm wraps the
// term losslessly (mirrors buildTracker.buildSearchOrFilter).
export function jobRunSearchOrFilter(rawTerm: string): string {
  const t = escapeOrIlikeTerm(rawTerm.trim());
  return `job_name.ilike.${t}`;
}
